import { useLayoutStore } from '../../state/layout.store'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import PackageModel from '@geartrack/shared-lib/dist/PackageModel'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import usePackagesRepository from '../../hooks/usePackagesRepository'
import { suggestNewCarrier } from '../../libraries/firestore'
import { notifyAndLog } from '../../libraries/utils'

const SuggestCarrierModal = () => {
  const suggestPkg = useLayoutStore(state => state.carrierSuggestModal)
  const suggestPkgToggle = useLayoutStore(state => state.carrierSuggestModalToggle)

  const onClose = useCallback(() => {
    suggestPkgToggle(null)
  }, [suggestPkgToggle])

  if (!suggestPkg) {
    // performance improvement
    return null
  }

  return (
    <Modal size="lg" show={true} onHide={onClose}>
      <CarrierShareDialogBody onClose={onClose} pkg={suggestPkg} />
    </Modal>
  )
}

const schema = t => {
  return yup.object().shape({
    link: yup
      .string()
      .url(t('suggest modal.link.validation url'))
      .required(t('suggest modal.link.validation required')),
    observations: yup.string().max(500),
    notify: yup.boolean(),
    email: yup
      .string()
      .email(t('suggest modal.email.validation email'))
      .when('notify', {
        is: true,
        then: yup.string().required(t('suggest modal.email.validation required')),
      }),
  })
}

const initialValues = {
  link: '',
  observations: '',
  notify: false,
  email: '',
}

const CarrierShareDialogBody = ({ onClose, pkg }: { onClose: any; pkg: PackageModel }) => {
  const { t } = useTranslation('packages')
  const pkgRepo = usePackagesRepository()

  const onSubmit = useCallback(
    values => {
      // we don't wait for it
      suggestNewCarrier(pkg, values.link, values.observations, values.email).catch(notifyAndLog)
      pkgRepo.updatePackage(pkg, {
        siteContext: {
          ...pkg.siteContext,
          suggested: values.link,
        },
      })

      toast.success(t('suggest modal.success toast'))
      onClose()
    },
    [onClose, pkg, pkgRepo, t]
  )

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: schema(t),
  })

  return (
    <form onSubmit={formik.handleSubmit}>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('suggest modal.title')} - {pkg.trackingId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{t('suggest modal.description')}</p>
        <p className="mb-4">
          <strong>{t('suggest modal.warning')}</strong>
        </p>

        <Form.Group as={Row} className="mb-3" controlId="link">
          <Form.Label column sm={2}>
            Tracking Link
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              type="text"
              placeholder={t('suggest modal.link.placeholder')}
              value={formik.values.link}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={isInvalid(formik, 'link')}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.link as any}</Form.Control.Feedback>
            <Form.Text className="text-muted">{t('suggest modal.link.help')}</Form.Text>
          </Col>
        </Form.Group>

        <Form.Group as={Row} className="mb-3" controlId="observations">
          <Form.Label column sm={2}>
            Observations
          </Form.Label>
          <Col sm={10}>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder={t('suggest modal.observations.placeholder')}
              value={formik.values.observations}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={isInvalid(formik, 'observations')}
            />
            <Form.Control.Feedback type="invalid">{formik.errors.observations as any}</Form.Control.Feedback>
          </Col>
        </Form.Group>

        <Form.Check
          type="switch"
          id="notify"
          name="notify"
          value={formik.values.notify}
          onChange={formik.handleChange}
          label={t('suggest modal.notify')}
        />

        {formik.values.notify && (
          <Form.Group as={Row} className="mb-3" controlId="email">
            <Form.Label column sm={2}>
              Email
            </Form.Label>
            <Col sm={10}>
              <Form.Control
                type="email"
                placeholder={t('suggest modal.email.placeholder')}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={isInvalid(formik, 'email')}
              />
              <Form.Control.Feedback type="invalid">{formik.errors.email as any}</Form.Control.Feedback>
              <Form.Text className="text-muted">{t('suggest modal.email.help')}</Form.Text>
            </Col>
          </Form.Group>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('share modal.close')}
        </Button>
        <Button variant="primary" type="submit">
          {t('suggest modal.submit')}
        </Button>
      </Modal.Footer>
    </form>
  )
}

function isInvalid(formik, fieldName) {
  return Boolean(formik.errors[fieldName] && formik.touched[fieldName])
}

export default SuggestCarrierModal
